import { useCallback, useEffect, useState } from "react";
import {
  FranchiseHeader,
  FranchisePubsListWrapper,
  FranchisePubsWrapper,
  RunnerWrapper,
} from "./style";
import { Cafe, PubTournament } from "../../../api/types";
import moment from "moment";
import { cafeTournaments, getFranchisePub } from "../../../api/cafe";
import { FranchiseItem } from "./component/FranchiseItem";
import { useHistory } from "react-router-dom";
import FranchiseMore from "./component/franchiseMore";
import useGeoLocation from "../../../hooks/useGeoLocation";
import { insetState } from "../../../recoil/inset";
import { useRecoilValue } from "recoil";
import LocationSheet from "../../../components/web/LocationSheet";
import FranchiseAreaFilter from "./component/franchiseAreaFilter";
import {
  getYoutubeLiveList,
  PubYoutubeLiveResponse,
} from "../../../api/youtube-live";

const FranchisePubs = () => {
  const [allList, setAllList] = useState<Cafe[]>([]);
  const [list, setList] = useState<Cafe[]>([]);
  const [selectedStore, setSelectedStore] = useState<Cafe | null>(null);
  const [showMoreOption, setShowMoreOption] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const { latitude, longitude, updateLocation } = useGeoLocation();
  const history = useHistory();
  const handleBackClick = () => history.goBack();
  const inset = useRecoilValue(insetState);
  const [selectedArea, setSelectedArea] = useState<{
    name: string;
    value: number[] | null;
  }>({
    name: "전체",
    value: null,
  });
  const [isMyPub, setIsMyPub] = useState<boolean>(false);

  // 내 위치 업데이트
  const updateMyLocation = useCallback(async () => {
    await updateLocation();
  }, [updateLocation]);

  // 초기 위치 설정 및 iOS 위치 권한 체크
  useEffect(() => {
    updateMyLocation();
    if (latitude === 37.493743896484375 && longitude === 127.0637597) {
      openDialog({
        title: "정확한 위치(ios)",
        type: "web",
        text: "더욱 정확한 위치 정보를 얻기 위해<br>아래와 같이 설정하세요.<br><br>설정 -> 러너러너 -> 위치 -> 정확한 위치 허용",
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        },
      });
    }
  }, []);

  const updateCafeListWithTournaments = useCallback(
    (
      cafeList: Cafe[],
      tournamentList: PubTournament[],
      lives: PubYoutubeLiveResponse[]
    ): Cafe[] => {
      // 각 카페에 해당하는 토너먼트 정보 업데이트
      const data = cafeList.map((cafe) => {
        const tournament = tournamentList.filter((tournament) => {
          const startDate = moment(tournament.startDate);
          const today = moment();
          const daysDiff = startDate.diff(today, "days");

          return tournament.cafeId === +cafe.id && daysDiff >= -1;
        });

        const live = lives.find((live) => live.pubId === cafe.id);

        return {
          ...cafe,
          pubTournamentList: tournament ? tournament : [],
          youtubeLiveList: live ? live.youtubeLiveList : [],
        };
      });

      return data;
    },
    []
  );

  const handleMoveRunnerFestival = () => {
    const festival = allList.find((item) => item.pubType === "RUNNER");
    if (festival) {
      history.push(`/store/${festival.id}?type=PREMIUM`);
    }
  };

  // 모든 필터를 동시에 적용하는 통합 필터링 함수
  const applyAllFilters = useCallback(() => {
    let filteredList = [...allList];

    // 1. 검색 필터 적용
    if (search.trim() !== "") {
      filteredList = filteredList.filter((item) =>
        item.cafeName.toLowerCase().includes(search.toLowerCase())
      );
    }

    // 2. 지역 필터 적용
    if (selectedArea.name !== "전체" && selectedArea.value) {
      filteredList = filteredList.filter((item) =>
        selectedArea.value?.includes(item.areaProvinceId)
      );
    }

    // 3. 내 펍 필터 적용
    if (isMyPub) {
      filteredList = filteredList.filter((item) => item.like);
    }

    setList(filteredList);
  }, [allList, search, selectedArea, isMyPub]);

  // 필터 조건이 변경될 때마다 통합 필터링 실행
  useEffect(() => {
    applyAllFilters();
  }, [applyAllFilters]);

  const getPubList = async () => {
    const res = await getFranchisePub(latitude, longitude);
    const pubIds = res.list.map((pub) => pub.id);
    const live = await getYoutubeLiveList(pubIds);

    const tournamentList = await cafeTournaments(res.list);
    if (tournamentList.length > 0) {
      const updatedList = updateCafeListWithTournaments(
        res.list,
        tournamentList,
        live
      );
      setAllList(updatedList);
      return;
    }
    setAllList(res.list);
  };

  const handleSearch = () => {
    // 검색 상태가 변경되면 통합 필터링이 자동으로 실행됨
    applyAllFilters();
  };

  useEffect(() => {
    getPubList();
  }, [latitude, longitude]);

  return (
    <FranchisePubsWrapper isApp={inset}>
      <FranchiseHeader isApp={inset}>
        <div className="close" onClick={handleBackClick}>
          <img
            src="/image-web/Icon/Back.svg"
            id="홀덤펍 찾기 뒤로가기"
            alt="close"
          />
        </div>
        <div className="input-wrapper">
          <input
            placeholder="홀덤 펍을 검색해보세요"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <img
            src="image-web/search/search-icon.svg"
            alt="icon"
            onClick={() => handleSearch()}
          />
        </div>
      </FranchiseHeader>
      <LocationSheet />
      {showMoreOption && selectedStore && (
        <FranchiseMore
          cafe={selectedStore}
          setShowMoreOption={setShowMoreOption}
          setSelectedStore={setSelectedStore}
          showMoreOption={showMoreOption}
        />
      )}
      <FranchiseAreaFilter
        isMyPub={isMyPub}
        setIsMyPub={setIsMyPub}
        setArea={setSelectedArea}
        selectedArea={selectedArea}
      />
      <RunnerWrapper isApp={inset}>
        <FranchisePubsListWrapper>
          {list
            .filter((item) => item.pubType !== "RUNNER")
            .map((item, index) => (
              <FranchiseItem
                item={item}
                key={index}
                setSelectedStore={setSelectedStore}
                setShowMoreOption={setShowMoreOption}
              />
            ))}
        </FranchisePubsListWrapper>
      </RunnerWrapper>
    </FranchisePubsWrapper>
  );
};

export default FranchisePubs;
function openDialog(arg0: {
  title: string;
  type: string;
  text: string;
  style: {
    display: string;
    justifyContent: string;
    alignItems: string;
    height: string;
  };
}) {
  throw new Error("Function not implemented.");
}
